import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, Store, Plus, Pencil } from "lucide-react";


const vendorName = (s) => s.businessName || s.fullName || "Unnamed vendor";

const searchText = (s) =>
    `${s.businessName || ""} ${s.fullName || ""} ${s.mobNo || ""} ${s.emailId || ""}`.toLowerCase();

const SupplierPicker = ({ suppliers = [], value, onChange, invalid }) => {
    const uid = useRef(`sp-${Math.random().toString(36).slice(2, 8)}`).current;
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const nameRef = useRef(null);
    const focusNameNext = useRef(false);

    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);

    const selected = useMemo(
        () => (value.supplier ? suppliers.find((s) => s._id === value.supplier) : null),
        [suppliers, value.supplier]
    );

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? suppliers.filter((s) => searchText(s).includes(q)) : suppliers;
        return list.slice(0, 50);
    }, [suppliers, query]);

    // last option is always "add manually"
    const options = useMemo(
        () => [...results.map((s) => ({ type: "supplier", s })), { type: "add" }],
        [results]
    );

    useEffect(() => {
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    useEffect(() => {
        if (!open) return;
        document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView({ block: "nearest" });
    }, [active, open, uid]);

    useEffect(() => {
        if (value.isManual && focusNameNext.current) {
            nameRef.current?.focus();
            focusNameNext.current = false;
        }
    }, [value.isManual]);

    const pickSupplier = (s) => {
        onChange({ supplier: s._id, name: "", isManual: false });
        setQuery("");
        setOpen(false);
    };

    // "+" row: trigger modal
    const goManual = () => {
        if (typeof value.onAddClick === "function") {
            value.onAddClick(query.trim());
        }
        setQuery("");
        setOpen(false);
    };

    const backToList = () => onChange({ supplier: "", name: "", isManual: false });

    const choose = (opt) => {
        if (!opt) return;
        if (opt.type === "add") return goManual();
        pickSupplier(opt.s);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, options.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && open) {
            e.preventDefault();
            choose(options[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const borderCls = invalid
        ? "border-red-400 focus:ring-red-200"
        : "border-slate-300 focus:ring-[#d4af37]/50 focus:border-transparent";

    /* ---------------- vendor chosen ---------------- */
    if (value.supplier) {
        return (
            <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white px-3 py-2">
                <span className="w-10 h-10 shrink-0 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                    <Store size={18} className="text-slate-400" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{selected ? vendorName(selected) : "Selected vendor"}</p>
                    {selected && (selected.fullName || selected.mobNo) && (
                        <p className="text-xs text-slate-500 truncate">
                            {[selected.businessName ? selected.fullName : "", selected.mobNo].filter(Boolean).join(" · ")}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        backToList();
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    className="text-xs font-medium text-blue-600 hover:underline shrink-0"
                >
                    Change
                </button>
            </div>
        );
    }

    /* ---------------- search box ---------------- */
    return (
        <div ref={wrapRef} className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    setActive(0);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="Search vendor by name, phone or email…"
                role="combobox"
                aria-expanded={open}
                aria-controls={`${uid}-list`}
                aria-activedescendant={open ? `${uid}-opt-${active}` : undefined}
                aria-autocomplete="list"
                aria-invalid={invalid || undefined}
                className={`w-full rounded-lg border pl-9 pr-9 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 ${borderCls}`}
            />
            {query && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                        setQuery("");
                        inputRef.current?.focus();
                    }}
                    className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                    <X size={16} />
                </button>
            )}

            {open && (
                <ul
                    id={`${uid}-list`}
                    role="listbox"
                    className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl py-1"
                >
                    {results.length === 0 && (
                        <li className="px-3 py-2.5 text-sm text-slate-500" role="presentation">
                            {suppliers.length === 0 ? "No vendors found yet." : "No vendor matches your search."}
                        </li>
                    )}

                    {options.map((opt, i) => {
                        const isActive = i === active;
                        if (opt.type === "add") {
                            return (
                                <li
                                    key="add"
                                    id={`${uid}-opt-${i}`}
                                    role="option"
                                    aria-selected={isActive}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => choose(opt)}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer border-t border-slate-100 ${isActive ? "bg-blue-50" : "bg-white"
                                        }`}
                                >
                                    <span className="w-9 h-9 shrink-0 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center">
                                        <Plus size={16} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block font-medium text-white truncate">
                                            {query.trim() ? `Add “${query.trim()}” as a new vendor` : "Can't find the vendor? Add a new vendor"}
                                        </span>
                                        <span className="block text-xs text-slate-500">Opens the Add Vendor modal</span>
                                    </span>
                                </li>
                            );
                        }

                        const { s } = opt;
                        return (
                            <li
                                key={s._id}
                                id={`${uid}-opt-${i}`}
                                role="option"
                                aria-selected={isActive}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(opt)}
                                className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer ${isActive ? "bg-slate-100" : "bg-white"}`}
                            >
                                <span className="w-9 h-9 shrink-0 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                                    <Store size={16} className="text-slate-400" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block font-medium text-white truncate">{vendorName(s)}</span>
                                    <span className="block text-xs text-slate-500 truncate">
                                        {[s.businessName ? s.fullName : "", s.mobNo, s.emailId].filter(Boolean).join(" · ") || "--"}
                                    </span>
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default SupplierPicker;